import { useEffect, useState } from "react";
import { FileStack, X } from "lucide-react";
import { api, dt, CASSE, ESIGIBILITA, NATURE, ALIQUOTE } from "@/lib/api";
import { Field, Panel } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";

const Pick = ({ value, onChange, options, testid, none }) => (
  <Select value={value || "_"} onValueChange={(v) => onChange(v === "_" ? "" : v)}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent>
      {none && <SelectItem value="_">{none}</SelectItem>}
      {Object.entries(options).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const CassaFields = ({ c, setC, forfettario }) => (
  <div className="grid gap-4 sm:grid-cols-4">
    <Field label="Cassa previdenziale" className="sm:col-span-2">
      <Pick testid="invoice-cassa-tipo" value={c.tipo} none="Nessuna" onChange={(v) => setC({ ...c, tipo: v, ...(c.tipo ? {} : { aliquota_iva: forfettario ? 0 : 22, natura: forfettario ? "N2.2" : "" }) })}
        options={Object.fromEntries(Object.entries(CASSE).map(([k, v]) => [k, `${k} · ${v}`]))} />
    </Field>
    {c.tipo && <>
      <Field label="Aliquota cassa %"><Input data-testid="invoice-cassa-aliquota" type="number" step="0.01" value={c.aliquota} onChange={(e) => setC({ ...c, aliquota: e.target.value })} /></Field>
      <Field label="IVA sulla cassa">
        <Pick testid="invoice-cassa-iva" value={String(c.aliquota_iva)} onChange={(v) => setC({ ...c, aliquota_iva: Number(v), natura: Number(v) ? "" : c.natura || "N2.2" })}
          options={Object.fromEntries(ALIQUOTE.map((a) => [String(a), `${a}%`]))} />
      </Field>
      {!Number(c.aliquota_iva) && <Field label="Natura cassa" className="sm:col-span-4"><Pick testid="invoice-cassa-natura" value={c.natura || "N2.2"} onChange={(v) => setC({ ...c, natura: v })} options={NATURE} /></Field>}
      {!forfettario && <label className="flex items-center gap-2 text-sm sm:col-span-4"><Checkbox data-testid="invoice-cassa-ritenuta" checked={!!c.ritenuta} onCheckedChange={(v) => setC({ ...c, ritenuta: !!v })} />Contributo cassa soggetto a ritenuta d'acconto</label>}
    </>}
  </div>
);

export const ScontoFields = ({ s, setS }) => (
  <div className="grid gap-4 sm:grid-cols-3">
    <Field label="Sconto / maggiorazione sul totale">
      <Pick testid="invoice-sconto-tipo" value={s.tipo} none="Nessuno" onChange={(v) => setS({ ...s, tipo: v })} options={{ SC: "Sconto", MG: "Maggiorazione" }} />
    </Field>
    {s.tipo && <>
      <Field label="Percentuale %"><Input data-testid="invoice-sconto-perc" type="number" step="0.01" value={s.percentuale} onChange={(e) => setS({ ...s, percentuale: e.target.value, importo: 0 })} /></Field>
      <Field label="oppure Importo €"><Input data-testid="invoice-sconto-importo" type="number" step="0.01" value={s.importo} onChange={(e) => setS({ ...s, importo: e.target.value, percentuale: 0 })} /></Field>
    </>}
  </div>
);

export const AltriDatiPanel = ({ f, set, forfettario }) => {
  const inp = (k, label, max, type = "text") => (
    <Field label={label}><Input data-testid={`invoice-${k}-input`} type={type} maxLength={max} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} /></Field>
  );
  return (
    <Panel title="Altri dati gestionali">
      <div className="space-y-6">
        <CassaFields c={f.cassa} setC={(v) => set("cassa", v)} forfettario={forfettario} />
        <div className="grid gap-4 sm:grid-cols-2">
          {inp("riferimento_amministrazione", "Riferimento amministrazione (max 20)", 20)}
          <Field label="Esigibilità IVA"><Pick testid="invoice-esigibilita" value={f.esigibilita} onChange={(v) => set("esigibilita", v || "I")} options={ESIGIBILITA} /></Field>
        </div>
        <ScontoFields s={f.sconto_totale} setS={(v) => set("sconto_totale", v)} />
      </div>
    </Panel>
  );
};

export const CollegataFields = ({ f, set, required }) => (
  <div className="mt-4 grid gap-4 rounded-lg border border-sky-100 bg-sky-50/50 p-4 sm:grid-cols-2">
    <p className="text-xs text-sky-900 sm:col-span-2">
      {required ? "Autofattura/integrazione: indica la fattura ricevuta dal fornitore. Il codice destinatario usato è il TUO (Impostazioni), non XXXXXXX." : "Documento collegato (facoltativo)"}
    </p>
    <Field label="N. fattura del fornitore / collegata"><Input data-testid="invoice-collegata-numero" value={f.collegata_numero || ""} onChange={(e) => set("collegata_numero", e.target.value)} /></Field>
    <Field label="Data documento"><DateField clearable testid="invoice-collegata-data" value={f.collegata_data || ""} onChange={(v) => set("collegata_data", v)} /></Field>
  </div>
);

export const DdtPicker = ({ customerId, selected, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [pick, setPick] = useState([]);
  useEffect(() => {
    if (open) {
      setPick([]);
      api.get("/ddt", { params: { customer_id: customerId, aperti: true } }).then((r) => setList(r.data.filter((d) => !selected.includes(d.id))));
    }
  }, [open, customerId, selected]);
  const toggle = (id) => setPick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <>
      <Button size="sm" variant="outline" disabled={!customerId} data-testid="import-ddt-button" onClick={() => setOpen(true)}><FileStack className="mr-1 h-4 w-4" />Da DDT</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>DDT da fatturare</DialogTitle></DialogHeader>
          {list.length === 0 ? <p className="text-sm text-slate-500">Nessun DDT aperto per questo cliente.</p> : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {list.map((d) => (
                <label key={d.id} data-testid={`ddt-option-${d.id}`} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 p-3 text-sm hover:bg-slate-50">
                  <Checkbox checked={pick.includes(d.id)} onCheckedChange={() => toggle(d.id)} />
                  <span className="font-mono">n. {d.numero}</span><span className="text-slate-500">{dt(d.data)}</span><span className="ml-auto text-xs text-slate-500">{d.lines.length} righe</span>
                </label>
              ))}
            </div>
          )}
          <DialogFooter><Button data-testid="confirm-ddt-button" disabled={!pick.length} onClick={() => { onAdd(list.filter((d) => pick.includes(d.id))); setOpen(false); }}>Inserisci in fattura</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DdtChips = ({ ddts, onRemove }) => (
  <div className="mb-3 flex flex-wrap gap-2">
    {ddts.map((d) => (
      <span key={d.id} data-testid={`ddt-chip-${d.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 font-mono text-xs">
        DDT {d.numero} del {dt(d.data)}
        <button type="button" onClick={() => onRemove(d.id)} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
      </span>
    ))}
  </div>
);
